import { Country, Currency } from './localisation.js'

export enum BillingCurrency {
	EUR = Currency.EUR,
	USD = Currency.USD,
	GBP = Currency.GBP,
}

export enum SubscriptionStatus {
	active = 'active',
	canceled = 'canceled',
	free = 'free',
	pastDue = 'pastDue',
	trialing = 'trialing',
	unpaid = 'unpaid',
}

export enum SubscriptionProduct {
	free = 'free',
	essential = 'essential',
	advanced = 'advanced',
	spendAddon = 'spendAddon',
	customAddon = 'customAddon',
}

export enum SubscriptionInterval {
	month = 'month',
	year = 'year',
}

export type SubscriptionIntervalDetails = {
	id: string
	price: Record<BillingCurrency, number>
}

export type SubscriptionPlanDetails = {
	id: string
	type: 'plan'
	rank: number
	excludedFeatures: SubscriptionFeature[]
	mktFeatures: SubscriptionMktFeature[]
	addons: SubscriptionProduct[]
} & Record<SubscriptionInterval, SubscriptionIntervalDetails>

export type SubscriptionAddonDetails = {
	id: string
	type: 'addon'
	features: SubscriptionFeature[]
	mktFeatures: SubscriptionMktFeature[]
} & Record<SubscriptionInterval, SubscriptionIntervalDetails>

export type SubscriptionProductDetails = SubscriptionPlanDetails | SubscriptionAddonDetails

export type SubscriptionProductDictionary = Record<SubscriptionProduct, SubscriptionProductDetails>

export enum SubscriptionMktFeature {
	free1 = 'featFree1',
	free2 = 'featFree2',
	free3 = 'featFree3',
	free4 = 'featFree4',
	essential0 = 'featEssential0',
	essential1 = 'featEssential1',
	essential2 = 'featEssential2',
	essential3 = 'featEssential3',
	essential4 = 'featEssential4',
	essential5 = 'featEssential5',
	essential6 = 'featEssential6',
	advanced0 = 'featAdvanced0',
	advanced1 = 'featAdvanced1',
	advanced2 = 'featAdvanced2',
	advanced3 = 'featAdvanced3',
	advanced4 = 'featAdvanced4',
	advanced5 = 'featAdvanced5',
	advanced6 = 'featAdvanced6',
	spendAddon = 'featSpendAddon',
	customAddon = 'featCustomAddon',
}

// to use with ensuteFeature
export enum SubscriptionFeature {
	accruals = 'accruals',
	quotas = 'quotas',
	approval = 'approval',
	currencyMulti = 'currencyMulti',
	billing = 'billing',
	costs = 'costs',
	expenses = 'expenses',
	budgets = 'budgets',
	tagsAdvanced = 'tagsAdvanced',
	customFields = 'customFields',
	customRoles = 'customRoles',
	ssoCustom = 'ssoCustom',
	auditTrail = 'auditTrail',
}

export type Subscription = {
	status?: SubscriptionStatus
	startDate?: number
	endDate?: number
	billingCurrency?: BillingCurrency
	cancelAt?: number
	portalURL?: string
	plan?: SubscriptionProduct
	interval?: SubscriptionInterval
	quantity?: number
	customerId?: string
	subscriptionId?: string
	subscriptionItemId?: string
	activeCount?: number
	total?: number
	activeAddons?: SubscriptionProduct[]
	addonItemIds?: Record<string, string>
}

type StripeIds = {
	productId: string
	monthlyPriceId: string
	yearlyPriceId: string
}

type PlanBase = {
	type: 'plan'
	rank: number
	excludedFeatures: SubscriptionFeature[]
	mktFeatures: SubscriptionMktFeature[]
	addons: SubscriptionProduct[]
	monthlyPrice: Record<BillingCurrency, number>
	yearlyPrice: Record<BillingCurrency, number>
}

type AddonBase = {
	type: 'addon'
	features: SubscriptionFeature[]
	mktFeatures: SubscriptionMktFeature[]
	monthlyPrice: Record<BillingCurrency, number>
	yearlyPrice: Record<BillingCurrency, number>
}

type ProductBase = PlanBase | AddonBase

const PRODUCT_BASE: Record<SubscriptionProduct, ProductBase> = {
	[SubscriptionProduct.free]: {
		type: 'plan',
		rank: 0,
		excludedFeatures: [
			SubscriptionFeature.accruals,
			SubscriptionFeature.quotas,
			SubscriptionFeature.approval,
			SubscriptionFeature.currencyMulti,
			SubscriptionFeature.billing,
			SubscriptionFeature.costs,
			SubscriptionFeature.expenses,
			SubscriptionFeature.budgets,
			SubscriptionFeature.tagsAdvanced,
			SubscriptionFeature.customFields,
			SubscriptionFeature.customRoles,
			SubscriptionFeature.ssoCustom,
			SubscriptionFeature.auditTrail,
		],
		mktFeatures: [
			SubscriptionMktFeature.free1,
			SubscriptionMktFeature.free2,
			SubscriptionMktFeature.free3,
			SubscriptionMktFeature.free4,
		],
		addons: [],
		monthlyPrice: { [Currency.EUR]: 0, [Currency.GBP]: 0, [Currency.USD]: 0 },
		yearlyPrice: { [Currency.EUR]: 0, [Currency.GBP]: 0, [Currency.USD]: 0 },
	},
	[SubscriptionProduct.essential]: {
		type: 'plan',
		rank: 1,
		excludedFeatures: [
			SubscriptionFeature.costs,
			SubscriptionFeature.expenses,
			SubscriptionFeature.budgets,
			SubscriptionFeature.tagsAdvanced,
			SubscriptionFeature.customFields,
			SubscriptionFeature.customRoles,
		],
		mktFeatures: [
			SubscriptionMktFeature.essential0,
			SubscriptionMktFeature.essential1,
			SubscriptionMktFeature.essential2,
			SubscriptionMktFeature.essential3,
			SubscriptionMktFeature.essential4,
			SubscriptionMktFeature.essential5,
			SubscriptionMktFeature.essential6,
		],
		addons: [SubscriptionProduct.spendAddon, SubscriptionProduct.customAddon],
		monthlyPrice: { [Currency.EUR]: 1000, [Currency.GBP]: 900, [Currency.USD]: 1000 },
		yearlyPrice: { [Currency.EUR]: 900, [Currency.GBP]: 800, [Currency.USD]: 900 },
	},
	[SubscriptionProduct.advanced]: {
		type: 'plan',
		rank: 2,
		excludedFeatures: [],
		mktFeatures: [
			SubscriptionMktFeature.advanced0,
			SubscriptionMktFeature.advanced1,
			SubscriptionMktFeature.advanced2,
			SubscriptionMktFeature.advanced3,
			SubscriptionMktFeature.advanced4,
			SubscriptionMktFeature.advanced5,
			SubscriptionMktFeature.advanced6,
		],
		addons: [],
		monthlyPrice: { [Currency.EUR]: 2000, [Currency.GBP]: 1800, [Currency.USD]: 2000 },
		yearlyPrice: { [Currency.EUR]: 1800, [Currency.GBP]: 1600, [Currency.USD]: 1800 },
	},
	[SubscriptionProduct.spendAddon]: {
		type: 'addon',
		features: [SubscriptionFeature.costs, SubscriptionFeature.expenses, SubscriptionFeature.budgets],
		mktFeatures: [SubscriptionMktFeature.spendAddon],
		monthlyPrice: { [Currency.EUR]: 500, [Currency.GBP]: 450, [Currency.USD]: 500 },
		yearlyPrice: { [Currency.EUR]: 450, [Currency.GBP]: 400, [Currency.USD]: 450 },
	},
	[SubscriptionProduct.customAddon]: {
		type: 'addon',
		features: [SubscriptionFeature.tagsAdvanced, SubscriptionFeature.customFields, SubscriptionFeature.customRoles],
		mktFeatures: [SubscriptionMktFeature.customAddon],
		monthlyPrice: { [Currency.EUR]: 500, [Currency.GBP]: 450, [Currency.USD]: 500 },
		yearlyPrice: { [Currency.EUR]: 450, [Currency.GBP]: 400, [Currency.USD]: 450 },
	},
}

// Stripe IDs — test mode (localhost, QA). To be updated when live IDs are available.
const STRIPE_IDS_TEST: Record<SubscriptionProduct, StripeIds> = {
	[SubscriptionProduct.free]: {
		productId: 'prod_free',
		monthlyPriceId: 'price_free',
		yearlyPriceId: 'price_free',
	},
	[SubscriptionProduct.essential]: {
		productId: 'prod_UYAdFZ3vyZqvZE',
		monthlyPriceId: 'price_1TZ4ITDxiWXoe2Udrfesuub0',
		yearlyPriceId: 'price_1TZ4ITDxiWXoe2UdjtCcfn4x',
	},
	[SubscriptionProduct.advanced]: {
		productId: 'prod_UYAfltpWODra8G',
		monthlyPriceId: 'price_1TZ4KaDxiWXoe2UdvPRU9x7H',
		yearlyPriceId: 'price_1TZ4KaDxiWXoe2UdJlyINOmH',
	},
	[SubscriptionProduct.spendAddon]: {
		productId: 'prod_UYAiFFgAjom67F',
		monthlyPriceId: 'price_1TZ4NDDxiWXoe2Ud2dWc6s8f',
		yearlyPriceId: 'price_1TZ4NDDxiWXoe2UdYFVet9rg',
	},
	[SubscriptionProduct.customAddon]: {
		productId: 'prod_UYAkaVfsmlXd6S',
		monthlyPriceId: 'price_1TZ4OxDxiWXoe2UdhXx6jkKM',
		yearlyPriceId: 'price_1TZ4OxDxiWXoe2UddQLJrdW6',
	},
}

// Stripe IDs — live mode (production). Starts as a copy of test; replace each entry
// with the real production Stripe IDs as they're provisioned.
const STRIPE_IDS_LIVE: Record<SubscriptionProduct, StripeIds> = {
	[SubscriptionProduct.free]: {
		productId: 'prod_free',
		monthlyPriceId: 'price_free',
		yearlyPriceId: 'price_free',
	},
	[SubscriptionProduct.essential]: {
		productId: 'prod_UXzbLkrwaZ4Xvy',
		monthlyPriceId: 'price_1TYtckDAdt6pxoOraLMFxwcl',
		yearlyPriceId: 'price_1TYtckDAdt6pxoOrO5EOxFwQ',
	},
	[SubscriptionProduct.advanced]: {
		productId: 'prod_UXzesAuZ7Iyr7K',
		monthlyPriceId: 'price_1TYteyDAdt6pxoOreg7A1obm',
		yearlyPriceId: 'price_1TYteyDAdt6pxoOr6tToGnOv',
	},
	[SubscriptionProduct.spendAddon]: {
		productId: 'prod_UXzjI1jkc6sTUE',
		monthlyPriceId: 'price_1TYtkODAdt6pxoOrglBZeN0a',
		yearlyPriceId: 'price_1TYtkODAdt6pxoOraGB5A8wK',
	},
	[SubscriptionProduct.customAddon]: {
		productId: 'prod_UXzm2TpskcXDL2',
		monthlyPriceId: 'price_1TYtmmDAdt6pxoOrvoulVHoX',
		yearlyPriceId: 'price_1TYtmmDAdt6pxoOrt7hqKAgN',
	},
}

function buildDictionary(ids: Record<SubscriptionProduct, StripeIds>): SubscriptionProductDictionary {
	const result = {} as SubscriptionProductDictionary
	for (const product of Object.values(SubscriptionProduct)) {
		const base = PRODUCT_BASE[product]
		const productIds = ids[product]
		const intervalMonth = { id: productIds.monthlyPriceId, price: base.monthlyPrice }
		const intervalYear = { id: productIds.yearlyPriceId, price: base.yearlyPrice }
		if (base.type === 'plan') {
			result[product] = {
				id: productIds.productId,
				type: 'plan',
				rank: base.rank,
				excludedFeatures: base.excludedFeatures,
				mktFeatures: base.mktFeatures,
				addons: base.addons,
				[SubscriptionInterval.month]: intervalMonth,
				[SubscriptionInterval.year]: intervalYear,
			}
		} else {
			result[product] = {
				id: productIds.productId,
				type: 'addon',
				features: base.features,
				mktFeatures: base.mktFeatures,
				[SubscriptionInterval.month]: intervalMonth,
				[SubscriptionInterval.year]: intervalYear,
			}
		}
	}
	return result
}

export const PRODUCT_DICTIONARY_TEST: SubscriptionProductDictionary = buildDictionary(STRIPE_IDS_TEST)
export const PRODUCT_DICTIONARY_LIVE: SubscriptionProductDictionary = buildDictionary(STRIPE_IDS_LIVE)

export function getProductDictionary(isLive: boolean): SubscriptionProductDictionary {
	return isLive ? PRODUCT_DICTIONARY_LIVE : PRODUCT_DICTIONARY_TEST
}

export function getProductName(product: SubscriptionProduct): string {
	return product.charAt(0).toUpperCase() + product.slice(1)
}

export function getProductFromId(stripeProductId: string, isLive: boolean): SubscriptionProduct | undefined {
	const dict = getProductDictionary(isLive)
	const entry = Object.entries(dict).find(([, details]) => details.id === stripeProductId)
	return entry ? (entry[0] as SubscriptionProduct) : undefined
}

export function getProductFromPriceId(priceId: string, isLive: boolean): SubscriptionProduct | undefined {
	const dict = getProductDictionary(isLive)
	const entry = Object.entries(dict).find(
		([, details]) =>
			details[SubscriptionInterval.month].id === priceId || details[SubscriptionInterval.year].id === priceId
	)
	return entry ? (entry[0] as SubscriptionProduct) : undefined
}

export function getIntervalFromPriceId(priceId: string, isLive: boolean): SubscriptionInterval | undefined {
	const dict = getProductDictionary(isLive)
	for (const details of Object.values(dict)) {
		if (details[SubscriptionInterval.month].id === priceId) return SubscriptionInterval.month
		if (details[SubscriptionInterval.year].id === priceId) return SubscriptionInterval.year
	}
	return undefined
}

export function hasFeature(
	subscription: {
		status?: string
		plan?: SubscriptionProduct
		activeAddons?: SubscriptionProduct[]
		// number for the backend (ms timestamp) / { ts } for the frontend's
		// BeeboleDate GraphQL serialization. The shared and frontend Subscription
		// types disagree on this field's shape; accept both rather than rely on
		// every caller to normalize.
		endDate?: number | { ts?: number } | null
	},
	feature: SubscriptionFeature
): boolean {
	if (subscription.status === 'trial' || subscription.status === SubscriptionStatus.trialing) {
		// Trials whose endDate has passed should not keep granting features just
		// because Stripe's trial_will_end webhook hasn't fired yet. An expired
		// trial has no plan/activeAddons (entity.ts only sets status/endDate/
		// billingCurrency), so falling through to the plan/addon checks returns
		// false — which is the intended locked-out behavior.
		const endDateTs = typeof subscription.endDate === 'number' ? subscription.endDate : subscription.endDate?.ts
		if (!endDateTs || endDateTs > Date.now()) return true
	}

	if (subscription.plan) {
		const plan = PRODUCT_BASE[subscription.plan]
		if (plan.type === 'plan' && !plan.excludedFeatures.includes(feature)) return true
	}

	for (const id of subscription.activeAddons ?? []) {
		const addon = PRODUCT_BASE[id]
		if (addon.type === 'addon' && addon.features.includes(feature)) return true
	}

	return false
}

export function getPlanRank(plan: SubscriptionProduct): number {
	const entry = PRODUCT_BASE[plan]
	return entry.type === 'plan' ? entry.rank : 0
}

export function isMainPlan(product: SubscriptionProduct): boolean {
	return PRODUCT_BASE[product].type === 'plan'
}

export function getBillingCurrency(country: Country): BillingCurrency {
	if (country === Country.GB) return BillingCurrency.GBP
	const usdCountry = [
		Country.AR,
		Country.BO,
		Country.BR,
		Country.BS,
		Country.BZ,
		Country.CA,
		Country.CL,
		Country.CO,
		Country.CR,
		Country.CU,
		Country.DO,
		Country.EC,
		Country.GF,
		Country.GT,
		Country.GY,
		Country.HN,
		Country.HT,
		Country.JM,
		Country.MX,
		Country.NI,
		Country.PA,
		Country.PE,
		Country.PR,
		Country.PY,
		Country.SR,
		Country.SV,
		Country.US,
		Country.UY,
		Country.VE,
		Country.AU,
		Country.NZ,
	]
	if (usdCountry.includes(country)) return BillingCurrency.USD
	return BillingCurrency.EUR
}

export const FREE_PLAN_SEATS = 5

export function hasAvailableSeats(activeCount: number, quantity?: number, status?: string): boolean {
	if (status === 'trial' || status === SubscriptionStatus.trialing) return true
	const seatLimit = quantity || FREE_PLAN_SEATS
	return activeCount < seatLimit
}

// Single source of truth for whether a subscription locks the organisation out of the product:
// the subscription was canceled, or a trial ran past its end date. Used by the GraphQL `locked`
// resolver, the per-request backend gate, and the sign-in check so they can never drift.
export function isLocked(subscription?: Subscription | null): boolean {
	if (!subscription) return false
	if (subscription.status === SubscriptionStatus.canceled) return true
	if (
		subscription.status === SubscriptionStatus.trialing &&
		subscription.endDate &&
		subscription.endDate < Date.now()
	)
		return true
	return false
}
